import React, { useState, useEffect } from 'react';

interface SidebarProps {
  sidebarOpen: boolean;
  onCloseSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ sidebarOpen, onCloseSidebar }) => {
  const url = window.location.href;
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isCurrentApp =
    url.includes('jlcconsulting') ||
    ((window as any).APP_URL && url.includes((window as any).APP_URL));

  const styles = {
    sidebar: {
      position: 'fixed' as const,
      top: 0,
      bottom: 0,
      left: 0,
      zIndex: 50,
      display: 'flex',
      flexDirection: 'column' as const,
      backgroundColor: '#ffffff',
      borderRight: '1px solid #e5e7eb',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1)',
      width: '320px',
      transform: isMobile ? (sidebarOpen ? 'translateX(0)' : 'translateX(-100%)') : 'translateX(0)'
    },
    header: {
      display: 'flex',
      height: '64px',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '0 16px',
      borderBottom: '1px solid #e5e7eb',
      position: 'relative' as const
    },
    logo: {
      height: '40px',
      width: 'auto',
      cursor: 'pointer'
    },
    closeButton: {
      position: 'absolute' as const,
      right: '16px',
      display: isMobile ? 'flex' : 'none',
      alignItems: 'center',
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      borderRadius: '8px',
      border: 'none',
      backgroundColor: 'transparent',
      cursor: 'pointer',
      transition: 'background-color 200ms',
    },
    closeButtonHover: {
      backgroundColor: '#f3f4f6'
    },
    closeIcon: {
      width: '20px',
      height: '20px',
      color: '#4b5563'
    },
    applicationsContainer: {
      flex: 1,
      padding: '24px 12px'
    },
    applicationsTitle: {
      fontSize: '12px',
      fontWeight: 600,
      color: '#9ca3af',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      marginBottom: '16px',
      paddingLeft: '12px'
    },
    appList: {
      display: 'flex',
      flexDirection: 'column' as const,
      gap: '8px'
    },
    appLink: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      borderRadius: '12px',
      transition: 'all 200ms',
      color: '#374151',
      textDecoration: 'none',
      border: '1px solid #e5e7eb',
      cursor: 'pointer'
    },
    appLinkHover: {
      backgroundColor: '#f9fafb',
      borderColor: '#d1d5db'
    },
    appLinkDisabled: {
      display: 'flex',
      alignItems: 'center',
      padding: '16px',
      borderRadius: '12px',
      transition: 'all 200ms',
      color: '#9ca3af',
      cursor: 'not-allowed',
      backgroundColor: '#f9fafb',
      border: '1px solid #e5e7eb'
    },
    appContent: {
      flex: 1
    },
    appTitle: {
      fontWeight: 600,
      color: '#111827',
      marginBottom: '4px'
    },
    appTitleDisabled: {
      fontWeight: 600,
      color: '#6b7280',
      marginBottom: '4px'
    },
    appDescription: {
      fontSize: '14px',
      color: '#6b7280'
    },
    appDescriptionDisabled: {
      fontSize: '14px',
      color: '#9ca3af'
    },
    appEmoji: {
      fontSize: '18px',
      marginLeft: '12px'
    }
  };

  const [hoveredApp, setHoveredApp] = useState<string | null>(null);
  const [hoveredClose, setHoveredClose] = useState(false);

  const handleLogoClick = () => {
    window.dispatchEvent(new CustomEvent('navigate', { detail: { page: 'home' } }));
  };

  return (
    <aside style={styles.sidebar}>
      {/* Header avec logo */}
      <div style={styles.header}>
        <img
          src="/image.png"
          alt="JLC Logo"
          style={styles.logo}
          onClick={handleLogoClick}
        />
        <button
          onClick={onCloseSidebar}
          onMouseEnter={() => setHoveredClose(true)}
          onMouseLeave={() => setHoveredClose(false)}
          style={{
            ...styles.closeButton,
            ...(hoveredClose ? styles.closeButtonHover : {})
          }}
        >
          <svg
            style={styles.closeIcon}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Applications Section */}
      <div style={styles.applicationsContainer}>
        <div style={styles.applicationsTitle}>
          Applications
        </div>
        <div style={styles.appList}>
          <a
            href="https://lwt.jlcconsulting.eu"
            onMouseEnter={() => setHoveredApp('lwt')}
            onMouseLeave={() => setHoveredApp(null)}
            style={{
              ...styles.appLink,
              ...(hoveredApp === 'lwt' ? styles.appLinkHover : {})
            }}
          >
            <div style={styles.appContent}>
              <div style={styles.appTitle}>Let's Work Together</div>
              <div style={styles.appDescription}>Application principale</div>
            </div>
            <span style={styles.appEmoji}>🏠</span>
          </a>

          <a
            href="https://lwt.jlcconsulting.eu/consultant/d/business"
            onMouseEnter={() => setHoveredApp('bizhunt')}
            onMouseLeave={() => setHoveredApp(null)}
            style={{
              ...styles.appLink,
              ...(hoveredApp === 'bizhunt' ? styles.appLinkHover : {})
            }}
          >
            <div style={styles.appContent}>
              <div style={styles.appTitle}>JLC BizHunt</div>
              <div style={styles.appDescription}>
                Le business est une forme de chasse aux trésors
              </div>
            </div>
            <span style={styles.appEmoji}>🚀</span>
          </a>

          <div
            onMouseEnter={() => setHoveredApp('talents')}
            onMouseLeave={() => setHoveredApp(null)}
            style={{
              ...styles.appLink,
              ...(hoveredApp === 'talents' ? styles.appLinkHover : {}),
              borderColor: isCurrentApp ? '#3b82f6' : '#e5e7eb',
              backgroundColor: isCurrentApp ? '#eff6ff' : 'transparent'
            }}
          >
            <div style={styles.appContent}>
              <div style={{
                ...styles.appTitle,
                color: isCurrentApp ? '#1e40af' : '#111827'
              }}>
                JLC Talents
              </div>
              <div style={styles.appDescription}>
                Les compétences enrichissent notre capacité à réussir
              </div>
            </div>
            {isCurrentApp && <span style={styles.appEmoji}>✨</span>}
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
